import { CONTENT } from '../content/runtime.js';
import { GROUND_FRAMES } from './gateflow.js';
import { PHASE, remainingTripFrames } from './operations.js';

/** One airport minute takes five seconds of simulation; pausing freezes the timetable. */
export const AIRPORT_MINUTE = CONTENT.balance.constants.AIRPORT_MINUTE;
export const BOARDING_SHORTLY_FRAMES = CONTENT.balance.constants.BOARDING_SHORTLY_FRAMES;
export const BOARDING_DESK_FRAMES = CONTENT.balance.constants.BOARDING_DESK_FRAMES;
export const DESK_DELAY_GRACE = CONTENT.balance.constants.DESK_DELAY_GRACE;
export const DEPARTURE_COUNTDOWN = CONTENT.balance.constants.DEPARTURE_COUNTDOWN;
const START_MINUTE = CONTENT.balance.constants.START_MINUTE;
export const TIMETABLE_KEYS = ['scheduledDepartureFrame', 'boardingOpensFrame', 'scheduledArrivalFrame', 'expectedArrivalFrame', 'boardingAnnouncedFrame', 'unattendedSince', 'aircraftReadyFrame', 'boardingStartedFrame', 'departureReadyFrame'] as const;
export interface TimedFlight {
    gate: number; flightNumber: number; phase: number; boardingCalled?: boolean; clock: number; tripFrames: number; scheduleFrames: number; nextDepartureFrame: number;
    scheduledDepartureFrame: number; boardingOpensFrame: number; scheduledArrivalFrame: number; expectedArrivalFrame: number;
    departureOffset: number; arrivalOffset: number;
    boardingStartedFrame?: number; boardingAnnouncedFrame?: number; boardingDeskFrames?: number; unattendedSince?: number; aircraftReadyFrame?: number; departureReadyFrame?: number; boarded?: number; seats?: number;
}
export type FlightTiming = 'EARLY' | 'ON TIME' | 'DELAYED';
export type DepartureBoardStatus = FlightTiming | 'BOARDING' | 'EARLY DEPARTURE';
export function airportTime(frame: number): string {
    const minute = ((START_MINUTE + Math.floor(frame / AIRPORT_MINUTE)) % 1440 + 1440) % 1440;
    return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
}
export function clockHands(frame: number) {
    const seconds = START_MINUTE * 60 + frame / AIRPORT_MINUTE * 60;
    const minute = Math.floor(seconds / 60), second = seconds % 60;
    return { hour: (minute % 720) / 720 * Math.PI * 2, minute: (minute % 60) / 60 * Math.PI * 2, second: Math.min(1, second / 58) * Math.PI * 2 };
}
export function scheduleTurnaround(p: TimedFlight, frame: number) {
    const interval = 10 * AIRPORT_MINUTE, offset = 45 * AIRPORT_MINUTE + p.gate * 5 * AIRPORT_MINUTE;
    const isFirstFlight = p.flightNumber === 101 + p.gate * 100;
    const firstDeparture = isFirstFlight ? 45 * AIRPORT_MINUTE : 0;
    const earlyOffset = p.arrivalOffset < 0 ? Math.abs(p.arrivalOffset) : (p.departureOffset < 0 ? Math.abs(p.departureOffset) : 0);
    const leadTime = !isFirstFlight && earlyOffset > 0
        ? Math.max(6 * AIRPORT_MINUTE, 20 * AIRPORT_MINUTE - Math.floor(earlyOffset * 0.7))
        : 20 * AIRPORT_MINUTE;
    const earliest = Math.max(Math.floor(frame / AIRPORT_MINUTE) * AIRPORT_MINUTE + leadTime, p.nextDepartureFrame, firstDeparture);
    p.scheduledDepartureFrame = Math.ceil((earliest - offset) / interval) * interval + offset;
    p.boardingOpensFrame = p.scheduledDepartureFrame - 15 * AIRPORT_MINUTE;
    p.scheduledArrivalFrame = p.scheduledDepartureFrame + p.tripFrames + 2 * GROUND_FRAMES;
    p.expectedArrivalFrame = p.scheduledArrivalFrame;
    p.departureOffset = 0;
    p.boardingStartedFrame = -1; p.boardingAnnouncedFrame = -1; p.boardingDeskFrames = 0; p.unattendedSince = -1; p.aircraftReadyFrame = frame; p.departureReadyFrame = -1;
}
export function ensureTimetable(p: TimedFlight, frame: number) {
    if (p.scheduledDepartureFrame >= 0) return;
    scheduleTurnaround(p, frame);
    // Legacy saves already in flight retain their remaining trip instead of taking off again.
    if (p.phase !== PHASE.BOARDING && p.phase !== PHASE.READY) {
        p.expectedArrivalFrame = frame + remainingTripFrames(p);
        p.scheduledArrivalFrame = p.expectedArrivalFrame;
    }
}
export function boardingWindowOpen(p: TimedFlight, frame: number): boolean {
    return p.phase === PHASE.BOARDING && p.boardingOpensFrame >= 0 && (p.boardingCalled === true || frame >= p.boardingOpensFrame);
}
export function earlyBoardingFrame(p: TimedFlight): number { return Math.max(0, p.aircraftReadyFrame ?? 0); }
export function earlyBoardingWindowOpen(p: TimedFlight, frame: number): boolean {
    return p.phase === PHASE.BOARDING && p.scheduledDepartureFrame >= 0 && frame >= earlyBoardingFrame(p);
}
export function recordDeparture(p: TimedFlight, frame: number, conditions = 0) {
    p.departureOffset = frame - p.scheduledDepartureFrame;
    p.scheduledArrivalFrame = p.scheduledDepartureFrame + p.tripFrames + 2 * GROUND_FRAMES;
    p.expectedArrivalFrame = Math.max(frame + 2 * GROUND_FRAMES + Math.floor(AIRPORT_MINUTE / 2), frame + p.tripFrames + 2 * GROUND_FRAMES + conditions);
}
export function timingStatus(offset: number): FlightTiming {
    return offset < -AIRPORT_MINUTE ? 'EARLY' : offset > AIRPORT_MINUTE ? 'DELAYED' : 'ON TIME';
}
export function boardingDelayReason(p: TimedFlight, frame: number): string {
    if (!boardingWindowOpen(p, frame)) return '';
    if ((p.aircraftReadyFrame ?? 0) > frame) return 'Aircraft delayed';
    if ((p.unattendedSince ?? -1) >= 0 && frame - p.unattendedSince! >= DESK_DELAY_GRACE) return 'Desk unattended';
    return '';
}
export function departureStatus(p: TimedFlight, frame: number): FlightTiming {
    if (boardingDelayReason(p, frame)) return 'DELAYED';
    if (p.phase === PHASE.BOARDING || p.phase === PHASE.READY) {
        const late = Math.max(0, frame - p.scheduledDepartureFrame);
        return late <= DESK_DELAY_GRACE ? 'ON TIME' : timingStatus(late);
    }
    return timingStatus(p.departureOffset);
}
export function departureBoardStatus(p: TimedFlight, frame: number): DepartureBoardStatus {
    if (p.phase === PHASE.BOARDING || p.phase === PHASE.READY) {
        if (boardingDelayReason(p, frame)) return 'DELAYED';
        if (p.boardingCalled) {
            if (p.boarded === p.seats && (p.departureReadyFrame ?? -1) >= 0 && frame < p.scheduledDepartureFrame) return 'EARLY DEPARTURE';
            return 'BOARDING';
        }
    }
    return departureStatus(p, frame);
}
export function arrivalStatus(p: TimedFlight): FlightTiming {
    return timingStatus(p.expectedArrivalFrame - p.scheduledArrivalFrame);
}
export function boardingTimingText(p: TimedFlight, frame: number): string {
    if (p.boardingCalled) return 'Boarding now. Stay at the desk to check tickets.';
    if (frame >= p.boardingOpensFrame) return 'The boarding window is open. Finish questions and work at the desk.';
    if (frame >= earlyBoardingFrame(p)) return `Ready passengers can board now with an operator at this gate. Standard boarding ${airportTime(p.boardingOpensFrame)}.`;
    return `Standard boarding ${airportTime(p.boardingOpensFrame)}; aircraft ready ${airportTime(earlyBoardingFrame(p))}.`;
}
export function departureTimingText(p: TimedFlight, frame: number): string {
    const pending = p.phase === PHASE.BOARDING || p.phase === PHASE.READY;
    const late = pending ? Math.max(0, Math.floor((frame - p.scheduledDepartureFrame) / AIRPORT_MINUTE)) : 0;
    return `${departureBoardStatus(p, frame)} · Scheduled ${airportTime(p.scheduledDepartureFrame)}${late ? ` · ${late} airport min past schedule` : ''}`;
}
